import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Image as ImageIcon,
  Pencil,
  Trash2,
  Lock,
  Unlock,
  MapPin,
  User,
  Phone,
  Calendar,
  FileText,
  Users,
  Receipt,
  CalendarDays,
  Repeat,
  AlertCircle,
  AlertTriangle,
  WalletCards,
  MessageSquareWarning,
  Plus,
  Loader2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Empty } from '@/components/ui/empty'
import { jobsApi } from '@/services/jobs'
import jobWorkflowApi from '@/services/job-workflow'
import { assignmentsApi } from '@/services/assignments'
import { sumActualAssignmentPayouts, sumEstimatedAssignmentPayouts, isWorkerInvoicePayType } from '@/lib/assignment-pay'
import { formatCurrency, formatDate, formatNumber, cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import { canLockJob, canUnlockJob, getJobLockActionDisabledReason } from '@/lib/job-lock'
import {
  getJobEditBlockedMessage,
  getJobStatusChangeBlockedMessage,
  isJobModificationBlocked,
} from '@/lib/job-status'
import { navigateToJobsListReturnPath, resolveJobsListReturnPath } from '@/lib/jobs-list-navigation'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getJobCategoryColor, resolveJobCategoryKey } from '@/lib/job-list-display'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { getLocationReturnPath, resolveReturnToPath, withReturnTo } from '@/lib/return-navigation'
import { JobEditForm } from '@/components/jobs/job-edit-form'
import { JobScheduleSummaryCard } from '@/components/jobs/job-schedule-summary-card'
import { JobFinancialSummary } from '@/components/jobs/job-financial-summary'
import { JobInvoicesPanel } from '@/components/jobs/job-invoices-panel'
import { JobMediaPanel } from '@/components/jobs/job-media-panel'
import { ComplaintsPanel } from '@/components/jobs/job-complaints-panel'
import { WorkDayEntriesAttention } from '@/components/jobs/work-day-entries-attention'
import { JobChecklistPanel } from '@/components/jobs/job-checklist-panel'
import { JobChecklistTemplatePanel } from '@/components/jobs/job-checklist-template-panel'
import { JobWorkDayEntriesPanel } from '@/components/jobs/job-work-day-entries-panel'
import { JobSubEventsPanel } from '@/components/jobs/job-sub-events-panel'
import { JobExpensesPanel } from '@/components/jobs/job-expenses-panel'
import { JobInventoryPanel } from '@/components/jobs/job-inventory-panel'
import { JobPaymentsPanel } from '@/components/jobs/job-payments-panel'
import { JobAssignCrewPanel } from '@/components/jobs/job-assign-crew-panel'
import { JobAssignmentsTable } from '@/components/jobs/job-assignments-table'
import { ChecklistCompletionDialog } from '@/components/jobs/checklist-completion-dialog'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { WorkflowStatusSelect } from '@/components/jobs/workflow-status-select'
import { WorkDayEntryActionsMenu } from '@/components/workdays/work-day-entry-actions-menu'
import { WorkDayEntryScheduleDialog } from '@/components/workdays/work-day-entry-schedule-dialog'
import {
  canEditWorkDayEntrySchedule,
  getWorkDayEntryStatusLabel,
  isWorkDayEntryAllowedForJobCompletion,
  resolveWorkDayEntryStatusFromEntry,
} from '@/lib/workday-entry-status'
import type { Assignment, JobWorkflowTransition, WorkDayEntry } from '@/types'

const JOB_DETAIL_TAB_TRIGGER_CLASS =
  'h-7 w-auto flex-none shrink-0 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium shadow-none hover:bg-muted/60'

const formatHoursTotal = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '0 hrs'
  }

  const rounded = Number(value)
  const display = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.0+$|(?<=\.[0-9]*[1-9])0+$/u, '')
  return `${display} hrs`
}

const featureNames = (
  features?: Array<{ name?: string | null }> | null,
) => (features ?? [])
  .map((feature) => String(feature?.name || '').trim())
  .filter(Boolean)

const pluralize = (count: number, singular: string, plural = `${singular}s`) => (
  `${count} ${count === 1 ? singular : plural}`
)

const getWorkEntryStatusSummary = (entries: WorkDayEntry[]) => {
  const counts = new Map<string, number>()
  entries.forEach((entry) => {
    const status = resolveWorkDayEntryStatusFromEntry(entry) ?? entry.status
    const label = entry.status_label || getWorkDayEntryStatusLabel(status)
    counts.set(label, (counts.get(label) ?? 0) + 1)
  })

  return Array.from(counts.entries())
    .map(([label, count]) => `${count} ${label}`)
    .join(', ')
}

const PAY_TYPES_WITH_VISIBLE_AMOUNT = new Set(['per_hour', 'per_day', 'per_job'])

const payTypeAmountSuffix: Record<string, string> = {
  per_hour: '/hr',
  per_day: '/day',
  per_job: '/job',
}

const toPayAmount = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const amount = Number(value)
  return Number.isFinite(amount) ? amount : null
}

const firstPositivePayAmount = (...values: Array<string | number | null | undefined>) => {
  for (const value of values) {
    const amount = toPayAmount(value)
    if (amount !== null && amount > 0) {
      return amount
    }
  }

  return null
}

const resolveAssignmentPayscaleAmount = (assignment: Assignment) => {
  const payType = assignment.pay_type ?? assignment.pay_type_snapshot

  if (payType === 'per_hour') {
    return firstPositivePayAmount(
      assignment.pay_scale_base_pay,
      assignment.pay,
    )
  }

  if (payType === 'per_day') {
    return firstPositivePayAmount(
      assignment.pay_scale_base_pay,
      assignment.pay,
      assignment.pay_flat,
      assignment.day_pay_rate_snapshot,
    )
  }

  if (payType === 'per_job') {
    return firstPositivePayAmount(
      assignment.pay_scale_base_pay,
      assignment.pay,
      assignment.pay_flat,
      assignment.peak_pay_rate_snapshot,
    )
  }

  return null
}

const isWorkerInvoicePayTypeForEntry = (payType?: string | null) => isWorkerInvoicePayType(payType)

const formatAssignmentPayscale = (assignment: Assignment) => {
  const payType = assignment.pay_type ?? assignment.pay_type_snapshot ?? ''
  const label = assignment.pay_type_label || payType.replace(/_/g, ' ') || '-'

  if (!PAY_TYPES_WITH_VISIBLE_AMOUNT.has(payType)) {
    return { label, amountLabel: null }
  }

  const amount = resolveAssignmentPayscaleAmount(assignment)
  return {
    label,
    amountLabel: amount === null ? null : `${formatCurrency(amount)} ${payTypeAmountSuffix[payType] ?? ''}`.trim(),
  }
}

function OrderItemFeatureBadges({
  includedFeatures,
  excludedFeatures,
}: {
  includedFeatures?: Array<{ name?: string | null }> | null
  excludedFeatures?: Array<{ name?: string | null }> | null
}) {
  const included = featureNames(includedFeatures)
  const excluded = featureNames(excludedFeatures)

  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-emerald-700">Included</span>
        {included.length > 0 ? (
          included.map((name, featureIndex) => (
            <Badge key={`inc-${featureIndex}-${name}`} variant="outline" className="border-emerald-300 text-emerald-700">
              {name}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-muted-foreground">None</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-rose-700">Excluded</span>
        {excluded.length > 0 ? (
          excluded.map((name, featureIndex) => (
            <Badge key={`exc-${featureIndex}-${name}`} variant="outline" className="border-rose-300 text-rose-700">
              {name}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-muted-foreground">None</span>
        )}
      </div>
    </div>
  )
}

const statusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  assigned: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  on_hold: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
}

const priorityColors: Record<string, string> = {
  low: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
  medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  critical: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
}

const priorityLabels: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
  critical: 'Critical',
}

const toAuditActionLabel = (value: string) => value
  .split('_')
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
  .join(' ')

const recurrenceLabels: Record<string, string> = {
  none: 'Does not repeat',
  daily: 'Daily',
  weekly: 'Weekly',
  bi_weekly: 'Bi-weekly',
  monthly: 'Monthly',
  every_n_days: 'Every N days',
  weekly_days: 'Weekly on selected days',
  monthly_day: 'Monthly on a specific day',
}

const recurrenceWeekdayShortLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

type JobDetailLocationState = {
  returnTo?: string
}

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const labels = useWorkspaceLabels()
  const isEventPortal = labels.mode === 'event'
  const isProjectPortal = labels.mode === 'project'
  const entityLabel = labels.entity
  const deleteConfirmationPhrase = labels.deleteConfirmationPhrase
  const entityListPath = labels.basePath
  const entityDetailPath = (entityId: string | number) => withServiceInstanceId(`${entityListPath}/${entityId}`, serviceInstanceId)
  const currentReturnPath = getLocationReturnPath(location)
  const jobsListPath = resolveReturnToPath(
    location.search,
    resolveJobsListReturnPath(
      (location.state as JobDetailLocationState | null)?.returnTo,
      withServiceInstanceId(entityListPath, serviceInstanceId),
    ),
  )
  
  const isEditing = searchParams.get('edit') === 'true'
  const activeTab = searchParams.get('tab') || 'details'
  const assignmentTableDate = searchParams.get('date') || ''
  const selectedAuditAction = searchParams.get('audit_action') || ''
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [assignmentWorkdayEntries, setAssignmentWorkdayEntries] = useState<Assignment | null>(null)
  const [showJobCompletionChecklist, setShowJobCompletionChecklist] = useState(false)
  const [showJobCompletionBlockers, setShowJobCompletionBlockers] = useState(false)
  const [pendingWorkflowCompletionTransition, setPendingWorkflowCompletionTransition] = useState<{ transition: JobWorkflowTransition; note?: string } | null>(null)
  const [createWorkdayOpen, setCreateWorkdayOpen] = useState(false)
  const [createWorkdayDate, setCreateWorkdayDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [scheduleEditEntry, setScheduleEditEntry] = useState<{ entryId: string; jobId: string } | null>(null)

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams, { state: location.state, replace: true })
  }

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['job', id, serviceInstanceId ?? 'none'],
    queryFn: () => (isProjectPortal ? jobsApi.getProject : jobsApi.getJob)(id!, { service_instance_id: serviceInstanceId ?? undefined }),
    enabled: !!id,
  })

  const customerName = job?.client_name ?? job?.client_user_info?.name ?? job?.order_info?.customer_name
  const customerAddress = job?.address ?? job?.location ?? job?.location_info?.place ?? job?.order_info?.address
  const customerEmail = job?.email ?? job?.client_user_info?.email ?? job?.order_info?.email
  const customerPhone = job?.phone ?? job?.client_user_info?.phone ?? job?.order_info?.phone
  const hasCustomerDetails = Boolean(customerName || customerEmail || customerPhone || customerAddress)

  const assignmentsQuery = useQuery({
    queryKey: ['job-assignments', id],
    queryFn: () => assignmentsApi.getAssignments({ job_id: id!, page: 1, page_size: 100 }),
    enabled: !!id,
  })
  const cancellationReasonsQuery = useQuery({
    queryKey: ['job-cancellation-reasons', 'job', id],
    queryFn: () => jobWorkflowApi.runtime.getCancellationReasons('job', { entity_id: id! }),
    enabled: !!id,
  })

  const attentionWorkDayEntriesQuery = useQuery({
    queryKey: ['attention-work-day-entries-count', serviceInstanceId, id],
    queryFn: () => assignmentsApi.getWorkDayEntries({
      needs_supervisor_attention: true,
      job_id: id!,
      page_size: 1,
    }),
    enabled: !!id,
    staleTime: 30_000,
  })

  const jobWorkDayEntriesQuery = useQuery({
    queryKey: ['job-work-day-entries', String(id ?? '')],
    queryFn: () => assignmentsApi.getWorkDayEntries({
      job_id: id!,
      page: 1,
      page_size: 500,
    }),
    enabled: !!id,
    staleTime: 30_000,
  })

  const auditActionsQuery = useQuery({
    queryKey: ['job-audit-actions'],
    queryFn: () => jobsApi.getAuditActionOptions(),
    staleTime: 5 * 60 * 1000,
  })

  const jobAuditsQuery = useQuery({
    queryKey: ['job-audits', id, selectedAuditAction || 'all'],
    queryFn: () => jobsApi.getAudits({
      job: id!,
      action: selectedAuditAction || undefined,
      page: 1,
      page_size: 20,
    }),
    enabled: !!id,
    staleTime: 30_000,
  })

  const jobCompletionBlockingEntries = useMemo(
    () => (jobWorkDayEntriesQuery.data?.results ?? []).filter((entry) => {
      const status = resolveWorkDayEntryStatusFromEntry(entry) ?? entry.status
      return !isWorkDayEntryAllowedForJobCompletion(status)
    }),
    [jobWorkDayEntriesQuery.data?.results],
  )
  const jobCompletionBlockingSummary = useMemo(
    () => getWorkEntryStatusSummary(jobCompletionBlockingEntries),
    [jobCompletionBlockingEntries],
  )

  useEffect(() => {
    if (!showJobCompletionBlockers) {
      return
    }
    if (!pendingWorkflowCompletionTransition) {
      return
    }
    if (jobCompletionBlockingEntries.length > 0) {
      return
    }

    setShowJobCompletionBlockers(false)
    setShowJobCompletionChecklist(true)
    toast.success(`All blocking ${labels.workDayEntryPluralLower} are resolved. Continue ${entityLabel.toLowerCase()} completion.`)
  }, [
    jobCompletionBlockingEntries.length,
    pendingWorkflowCompletionTransition,
    showJobCompletionBlockers,
  ])

  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => jobsApi.delete(id, { reason }),
    onSuccess: () => {
      toast.success(`${entityLabel} deleted successfully`)
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      navigateToJobsListReturnPath(navigate, jobsListPath)
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      if (message.toLowerCase().includes('cannot complete job') && jobCompletionBlockingEntries.length > 0) {
        setShowJobCompletionBlockers(true)
        return
      }
      toast.error(message)
    },
  })

  const lockMutation = useMutation({
    mutationFn: ({ id, lock }: { id: string; lock: boolean }) =>
      lock ? jobsApi.lock(id) : jobsApi.unlock(id),
    onSuccess: (_, { lock }) => {
      toast.success(`${entityLabel} ${lock ? 'locked' : 'unlocked'} successfully`)
      queryClient.invalidateQueries({ queryKey: ['job', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const workflowStatusMutation = useMutation({
    mutationFn: ({ transition, note }: { transition: JobWorkflowTransition; note?: string }) => jobWorkflowApi.runtime.applyStatus({
      entity_type: 'job',
      entity_id: id as string,
      to_status: transition.to_status,
      note,
    }),
    onSuccess: () => {
      toast.success('Workflow status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['job', id] })
      queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', 'job', String(id)] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const generateNextOccurrenceMutation = useMutation({
    mutationFn: () => jobsApi.generateNextOccurrence(id as string),
    onSuccess: () => {
      toast.success('Next occurrence generated successfully')
      queryClient.invalidateQueries({ queryKey: ['job', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const createWorkdayMutation = useMutation({
    mutationFn: () => assignmentsApi.createWorkDay({
      job: String(job!.id),
      work_date: createWorkdayDate,
    }),
    onSuccess: () => {
      toast.success(`${labels.workDay} created successfully`)
      setCreateWorkdayOpen(false)
      queryClient.invalidateQueries({ queryKey: ['workdays'] })
      queryClient.invalidateQueries({ queryKey: ['job-work-day-entries', String(job!.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const estimatedWorkerPayoutTotal = useMemo(
    () => sumEstimatedAssignmentPayouts(assignmentsQuery.data?.results ?? []),
    [assignmentsQuery.data?.results],
  )
  const actualPayoutTotal = useMemo(
    () => sumActualAssignmentPayouts(assignmentsQuery.data?.results ?? []),
    [assignmentsQuery.data?.results],
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !job) {
    return (
      <Card>
        <CardContent className="py-10">
          <Empty>
            <div className="text-center">
              <p className="text-lg font-medium">{`${entityLabel} not found`}</p>
              <p className="text-sm text-muted-foreground">
                {error ? getErrorMessage(error) : `The ${entityLabel.toLowerCase()} you are looking for does not exist.`}
              </p>
            </div>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  const expensesPath = withReturnTo(
    withServiceInstanceId(`/finance/expenses?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId),
    currentReturnPath,
  )
  const particularsPath = withReturnTo(
    withServiceInstanceId(`/finance/particulars?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId),
    currentReturnPath,
  )
  const isRecurringJob = job.recurrence_rule && job.recurrence_rule !== 'none'
  let recurrenceSummary = recurrenceLabels[job.recurrence_rule ?? 'none']
  if (job.recurrence_rule === 'every_n_days' && job.recurrence_interval_days) {
    recurrenceSummary = `Every ${job.recurrence_interval_days} days`
  } else if (job.recurrence_rule === 'weekly_days' && job.recurrence_weekdays?.length) {
    const weekdayLabels = job.recurrence_weekdays
      .map((day) => recurrenceWeekdayShortLabels[day] || '')
      .filter(Boolean)
    if (weekdayLabels.length > 0) {
      recurrenceSummary = `Weekly on ${weekdayLabels.join(', ')}`
    }
  } else if (job.recurrence_rule === 'monthly_day' && job.recurrence_month_day) {
    recurrenceSummary = `Monthly on day ${job.recurrence_month_day}`
  }
  if (isRecurringJob && job.recurrence_count) {
    recurrenceSummary = `${recurrenceSummary} (${job.recurrence_count} left)`
  }
  const jobWorkflowStatusCode = job.workflow_status_code || null
  const jobWorkflowStatusLabel = job.workflow_status_label || 'Workflow not set'
  const jobCategoryName = job.category_name?.trim() || null
  const jobCategoryColor = getJobCategoryColor(resolveJobCategoryKey(job))
  const recurrenceLineage = [
    job.recurrence_parent_info
      ? {
          label: 'Source',
          title: formatJobTitleWithId({
            jobId: job.recurrence_parent_info.id,
            title: job.recurrence_parent_info.title,
          }),
          href: entityDetailPath(job.recurrence_parent_info.id ?? job.id),
          meta: job.recurrence_parent_info.status?.replace('_', ' ') || 'Unknown status',
          active: false,
        }
      : isRecurringJob
        ? {
            label: 'Source',
            title: formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref, entityLabel: labels.entity }),
            href: entityDetailPath(job.id),
            meta: 'Series template',
            active: true,
          }
        : null,
    isRecurringJob || job.recurrence_parent_info || job.next_occurrence_info
      ? {
          label: 'Current',
          title: formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref, entityLabel: labels.entity }),
          href: entityDetailPath(job.id),
          meta: jobWorkflowStatusLabel,
          active: true,
        }
      : null,
    job.next_occurrence_info
      ? {
          label: 'Next',
          title: formatJobTitleWithId({
            jobId: job.next_occurrence_info.id,
            title: job.next_occurrence_info.title,
          }),
          href: entityDetailPath(job.next_occurrence_info.id ?? job.id),
          meta: job.next_occurrence_info.scheduled_start
            ? `Scheduled ${formatDate(job.next_occurrence_info.scheduled_start)}`
            : 'Schedule not set',
          active: false,
        }
      : null,
  ].filter((item): item is {
    label: string
    title: string
    href: string
    meta: string
    active: boolean
  } => Boolean(item))
  const jobSummary = job.job_summary
  const jobAllowsUnlock = canUnlockJob(job)
  const jobAllowsLock = canLockJob(job)
  const jobLockActionDisabledReason = getJobLockActionDisabledReason(job)
  const isJobLockActionDisabled = job.is_locked ? !jobAllowsUnlock : !jobAllowsLock
  const jobModificationBlocked = isJobModificationBlocked(job)
  const jobEditBlockedMessage = getJobEditBlockedMessage(job)
  const jobStatusChangeBlockedMessage = getJobStatusChangeBlockedMessage(job)
  const jobEditDisabled = Boolean(job.has_invoice) || jobModificationBlocked
  const jobStatusChangeDisabled = Boolean(job.has_invoice) || jobModificationBlocked
  const workDayEntriesPath = withReturnTo(
    withServiceInstanceId(`${labels.basePath}/workdays?job=${encodeURIComponent(String(job.id))}`, serviceInstanceId),
    currentReturnPath,
  )
  const jobMapPath = withReturnTo(
    withServiceInstanceId(`/job/locations?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId),
    currentReturnPath,
  )

  if (isEditing) {
    if (jobEditDisabled) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => updateSearchParams({ edit: null })}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{`Edit ${entityLabel}`}</h1>
              <p className="text-sm text-muted-foreground">
                {jobEditBlockedMessage ?? `This ${entityLabel.toLowerCase()} cannot be edited.`}
              </p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => updateSearchParams({ edit: null })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{`Edit ${entityLabel}`}</h1>
            <p className="text-sm text-muted-foreground">{`Update ${entityLabel.toLowerCase()} details`}</p>
          </div>
        </div>
        <JobEditForm
          job={job}
          serviceInstanceId={serviceInstanceId}
          entityType={isProjectPortal ? 'project' : isEventPortal ? 'event' : 'job'}
          onSuccess={() => updateSearchParams({ edit: null })}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigateToJobsListReturnPath(navigate, jobsListPath)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{job.title}</h1>
              {jobCategoryName ? (
                <Badge className={cn('shrink-0 border-0 px-3 py-1.5 text-sm font-semibold', jobCategoryColor.badge)}>
                  {jobCategoryName}
                </Badge>
              ) : null}
              {isEventPortal && (job.tags ?? []).map((tag) => (
                <Badge key={String(tag.id)} variant="secondary" className="shrink-0 px-3 py-1.5 text-sm font-medium">
                  {tag.title}
                </Badge>
              ))}
              {job.is_locked && (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline">{`${entityLabel} #${job.id}`}</Badge>
              <Badge className={statusColors[jobWorkflowStatusCode || ''] || statusColors.draft}>
                {jobWorkflowStatusLabel}
              </Badge>
              <Badge variant="outline" className={priorityColors[job.priority] || priorityColors.medium}>
                {priorityLabels[job.priority] || job.priority}
              </Badge>
              {(job.team_template_info || job.team_template) ? (
                <Badge variant="outline" className="gap-1">
                  <Users className="h-3 w-3" />
                  Template: {job.team_template_info?.name || `#${job.team_template}`}
                </Badge>
              ) : null}
              {(job.staffed_team_info || job.staffed_team) ? (
                <Badge
                  variant="outline"
                  className={job.staffed_team_info?.is_ready === true
                    ? 'gap-1 border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'gap-1'}
                >
                  <Users className="h-3 w-3" />
                  Team: {job.staffed_team_info?.name || `#${job.staffed_team}`}
                </Badge>
              ) : null}
              <WorkDayEntriesAttention
                count={attentionWorkDayEntriesQuery.data?.count ?? 0}
                isLoading={attentionWorkDayEntriesQuery.isLoading}
                serviceInstanceId={serviceInstanceId}
                jobId={String(job.id)}
                returnPath={currentReturnPath}
              />
              {isRecurringJob && (
                <Badge variant="secondary" className="gap-1">
                  <Repeat className="h-3 w-3" />
                  {recurrenceSummary}
                </Badge>
              )}
              {job.parent_event_info ? (
                <Badge variant="outline" asChild>
                  <Link to={entityDetailPath(job.parent_event_info.id ?? job.id)}>
                    Parent: {formatJobTitleWithId({
                      jobId: job.parent_event_info.id,
                      title: job.parent_event_info.title,
                      jobRef: job.parent_event_info.job_ref,
                      entityLabel: labels.entity,
                    })}
                  </Link>
                </Badge>
              ) : null}
              {job.recurrence_parent_info && (
                <Badge variant="outline">{labels.recurringFromEntity}</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="sticky top-2 z-20">
            <WorkflowStatusSelect
              entityType="job"
              entityId={job.id}
              currentValue={jobWorkflowStatusLabel}
              disabled={jobStatusChangeDisabled}
              className="w-44"
              onApplied={() => queryClient.invalidateQueries({ queryKey: ['job', id] })}
              onSelectTransition={(transition, note) => {
                if (transition.to_status_code === 'completed' && jobWorkflowStatusCode !== 'completed') {
                  if (jobCompletionBlockingEntries.length > 0) {
                    setPendingWorkflowCompletionTransition({ transition, note })
                    setShowJobCompletionBlockers(true)
                    return
                  }
                  setPendingWorkflowCompletionTransition({ transition, note })
                  setShowJobCompletionChecklist(true)
                  return
                }
                workflowStatusMutation.mutate({ transition, note })
              }}
            />
          </div>

          <Button variant="outline" asChild>
            <Link to={jobMapPath}>
              <MapPin className="mr-2 h-4 w-4" />
              Show in map
            </Link>
          </Button>

          <Button variant="outline" asChild>
            <Link to={workDayEntriesPath}>
              <CalendarDays className="mr-2 h-4 w-4" />
              {labels.workDayPlural}
            </Link>
          </Button>

          {jobStatusChangeBlockedMessage && (
            <p className="text-xs text-muted-foreground">
              {jobStatusChangeBlockedMessage}
            </p>
          )}

          <Button
            variant="outline"
            onClick={() => lockMutation.mutate({ id: job.id, lock: !job.is_locked })}
            disabled={isJobLockActionDisabled}
          >
            {job.is_locked ? (
              <>
                <Unlock className="mr-2 h-4 w-4" />
                Unlock
              </>
            ) : (
              <>
                <Lock className="mr-2 h-4 w-4" />
                Lock
              </>
            )}
          </Button>
          {jobLockActionDisabledReason ? (
            <p className="text-xs text-muted-foreground">{jobLockActionDisabledReason}</p>
          ) : null}

          <Button
            variant="outline"
            onClick={() => updateSearchParams({ edit: 'true' })}
            disabled={jobEditDisabled}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
          {jobEditBlockedMessage && !jobLockActionDisabledReason ? (
            <p className="text-xs text-muted-foreground">
              {jobEditBlockedMessage}
            </p>
          ) : null}

          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            disabled={jobModificationBlocked}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {jobCompletionBlockingEntries.length > 0 ? (
        <Link
          to={workDayEntriesPath}
          className="block rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 transition-colors hover:border-amber-300 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70"
        >
          <div className="flex gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="space-y-1 text-sm">
              <p className="font-semibold">
                {pluralize(jobCompletionBlockingEntries.length, 'work entry', 'work entries')} still open or awaiting acceptance/review.
              </p>
              {jobCompletionBlockingSummary ? (
                <p>
                  Pending statuses: {jobCompletionBlockingSummary}.
                </p>
              ) : null}
              <p className="text-amber-900/80">
                {entityLabel} completion is blocked until {labels.workDayEntryPluralLower} are Approved, Rejected, or Draft. The assignment will close automatically when the last {labels.workDayEntryLower} is approved final.
              </p>
              <p className="text-xs font-medium text-amber-900 underline underline-offset-2">
                View {labels.workDayEntryPluralLower}
              </p>
            </div>
          </div>
        </Link>
      ) : null}

      {Number(job.workday_entry_clash_count ?? 0) > 0 ? (
        <Link
          to={workDayEntriesPath}
          className="block rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 transition-colors hover:border-amber-300 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70"
        >
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="space-y-1 text-sm">
              <p className="font-semibold">
                {pluralize(Number(job.workday_entry_clash_count ?? 0), 'schedule')} clash with another schedule for the same worker.
              </p>
              <p className="text-amber-900/80">
                Overlaps are allowed, but review worker availability before dispatch or completion.
              </p>
              <p className="text-xs font-medium text-amber-900 underline underline-offset-2">
                View {labels.workDayEntryPluralLower}
              </p>
            </div>
          </div>
        </Link>
      ) : null}

      <Tabs
        value={activeTab}
        onValueChange={(value) => updateSearchParams({ tab: value === 'details' ? null : value })}
        className="space-y-4"
      >
        <TabsList className="h-auto w-fit max-w-full flex-wrap justify-start gap-1 rounded-none bg-transparent p-0">
          <TabsTrigger value="details" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Details
          </TabsTrigger>
          <TabsTrigger value="media" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Media
          </TabsTrigger>
          <TabsTrigger value="assignments" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Assignments
          </TabsTrigger>
          <TabsTrigger value="payments" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Payments
          </TabsTrigger>
          <TabsTrigger value="invoices" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Invoices
          </TabsTrigger>
          <TabsTrigger value="expenses" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Expenses
          </TabsTrigger>
          <TabsTrigger value="particulars" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Inventory
          </TabsTrigger>
          <TabsTrigger value="workday-entries" className={`${JOB_DETAIL_TAB_TRIGGER_CLASS} gap-1`}>
            {labels.workDayEntryPlural}
            {(attentionWorkDayEntriesQuery.data?.count ?? 0) > 0 ? (
              <Badge variant="destructive" className="h-4 min-w-4 rounded-full px-1 text-[9px]">
                {attentionWorkDayEntriesQuery.data?.count}
              </Badge>
            ) : null}
            {Number(job.workday_entry_clash_count ?? 0) > 0 ? (
              <Badge variant="outline" className="h-4 min-w-4 rounded-full border-amber-300 bg-amber-50 px-1 text-[9px] text-amber-800">
                {job.workday_entry_clash_count}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="checklist" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Checklist
          </TabsTrigger>
          <TabsTrigger value="complaints" className={JOB_DETAIL_TAB_TRIGGER_CLASS}>
            Complaints
          </TabsTrigger>
          {isEventPortal ? (
            <TabsTrigger value="sub-events" className={`${JOB_DETAIL_TAB_TRIGGER_CLASS} gap-1`}>
              Sub Events
              {(job.sub_events_count ?? 0) > 0 ? (
                <Badge variant="secondary" className="h-4 min-w-4 rounded-full px-1 text-[9px]">
                  {job.sub_events_count}
                </Badge>
              ) : null}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <JobScheduleSummaryCard job={job} />

          <JobFinancialSummary
            job={job}
            serviceInstanceId={serviceInstanceId}
            expensesPath={expensesPath}
            particularsPath={particularsPath}
          />

          <div className="grid gap-4 md:grid-cols-2">
            {/* Job Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{entityLabel} Summary</CardTitle>
                <CardDescription>Operational totals from workdays and active worker assignments.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Work Days</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatNumber(jobSummary?.work_days_count ?? 0)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Logged Hours</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatHoursTotal(jobSummary?.total_hours ?? 0)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Active Workers</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatNumber(jobSummary?.total_workers ?? 0)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Internal Workers</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatNumber(jobSummary?.internal_workers ?? 0)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Contract Workers</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatNumber(jobSummary?.contract_workers ?? 0)}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Est. Worker Payout</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">
                      {assignmentsQuery.isLoading ? '…' : formatCurrency(estimatedWorkerPayoutTotal)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Per hour and per job assignments only
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{entityLabel} Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {job.job_code ? (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Code</p>
                      <p className="mt-1 text-sm">{job.job_code}</p>
                    </div>
                  ) : null}
                  {job.job_landmark ? (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Landmark</p>
                      <p className="mt-1 text-sm">{job.job_landmark}</p>
                    </div>
                  ) : null}
                </div>

                {job.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Description</p>
                    <p className="mt-1 text-sm">{job.description}</p>
                  </div>
                )}
                
                <Separator />
                
                <div className="grid gap-3">
                  {job.location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{job.location}</span>
                    </div>
                  )}
                  {job.address && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <span>{job.address}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Incharge</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {job.primary_worker_info ? (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <Link 
                        to={withServiceInstanceId(`/workers/${job.primary_worker}`, serviceInstanceId)}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {job.primary_worker_info.name || 'Unknown worker'}
                      </Link>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span>Worker ID: {job.primary_worker ?? '-'}</span>
                    </div>
                    {job.primary_worker_info.mobile ? (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{job.primary_worker_info.mobile}</span>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Phone not available</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No primary worker assigned</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Team Assignment</CardTitle>
                <CardDescription>Selected team template and staffed team for this job.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Team Template</p>
                  {job.team_template_info ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-foreground">{job.team_template_info.name || `Template #${job.team_template}`}</span>
                      {job.team_template_info.code ? (
                        <Badge variant="outline" className="text-[10px]">
                          {job.team_template_info.code}
                        </Badge>
                      ) : null}
                    </div>
                  ) : job.team_template ? (
                    <p className="text-sm text-foreground">Template #{job.team_template}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No team template selected</p>
                  )}
                </div>

                <Separator />

                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Staffed Team</p>
                  {job.staffed_team_info ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-foreground">{job.staffed_team_info.name || `Team #${job.staffed_team}`}</span>
                        {job.staffed_team_info.code ? (
                          <Badge variant="outline" className="text-[10px]">
                            {job.staffed_team_info.code}
                          </Badge>
                        ) : null}
                        {job.staffed_team_info.is_ready === true ? (
                          <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">Ready</Badge>
                        ) : (
                          <Badge variant="secondary">Not Ready</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Headcount: {job.staffed_team_info.member_count ?? 0} / {job.staffed_team_info.required_headcount ?? '-'}
                      </p>
                    </div>
                  ) : job.staffed_team ? (
                    <p className="text-sm text-foreground">Team #{job.staffed_team}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No staffed team selected</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Linked Order</CardTitle>
                <CardDescription>Booking packages and payment links for this job.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {job.order_reference_id ? (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">Order #{job.order_reference_id}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {[
                            job.order_info?.status ? `Status ${job.order_info.status}` : null,
                            job.order_info?.type ? `Type ${job.order_info.type}` : null,
                          ].filter(Boolean).join(' • ') || 'Linked from store service'}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={withServiceInstanceId(`/services/orders/${job.order_reference_id}#payments`, serviceInstanceId)}>
                          <Receipt className="mr-2 h-4 w-4" />
                          Order payments
                        </Link>
                      </Button>
                    </div>
                    <div className="grid gap-2 text-sm text-muted-foreground">
                      <div>
                        Booking placed: {job.order_info?.created_on
                          ? formatDate(job.order_info.created_on, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '-'}
                      </div>
                      {(job.order_item_info || job.order_item_features_snapshot) ? (
                        <div className="rounded-md border border-border/70 bg-muted/20 p-3">
                          {job.order_item_info ? (
                            <>
                              <p className="text-sm font-medium text-foreground">Linked line item</p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {[job.order_item_info.product_name, job.order_item_info.product_variant_name].filter(Boolean).join(' • ') || `Item #${job.order_item_info.id}`}
                              </p>
                              {job.order_item_info.qty != null ? (
                                <p className="text-xs text-muted-foreground">Qty {job.order_item_info.qty}</p>
                              ) : null}
                            </>
                          ) : (
                            <p className="text-sm font-medium text-foreground">Package features</p>
                          )}
                          <OrderItemFeatureBadges
                            includedFeatures={job.order_item_features_snapshot?.included_features}
                            excludedFeatures={job.order_item_features_snapshot?.excluded_features}
                          />
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">This job is not linked to an order.</p>
                )}
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {customerName ? (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    {job.client_user ? (
                      <Link
                        to={withServiceInstanceId(`/org/users/${job.client_user}`, serviceInstanceId)}
                        className="transition-colors hover:text-primary"
                      >
                        {customerName}
                      </Link>
                    ) : (
                      <span>{customerName}</span>
                    )}
                  </div>
                ) : null}
                {customerAddress ? (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>{customerAddress}</span>
                  </div>
                ) : null}
                {customerEmail ? (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>{customerEmail}</span>
                  </div>
                ) : null}
                {customerPhone ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{customerPhone}</span>
                  </div>
                ) : null}
                {!hasCustomerDetails ? (
                  <p className="text-sm text-muted-foreground">No customer information</p>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recurrence</CardTitle>
                <CardDescription>How this job participates in a recurring series.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Rule:</span>
                  <span>{recurrenceSummary}</span>
                </div>

                {recurrenceLineage.length > 0 && (
                  <div className="grid gap-2 md:grid-cols-3">
                    {recurrenceLineage.map((item) => (
                      <Link
                        key={`${item.label}-${item.href}`}
                        to={item.href}
                        className={`rounded-lg border p-3 text-sm transition-colors hover:border-primary/50 ${item.active ? 'border-primary/30 bg-primary/5' : 'border-border/70 bg-muted/20'}`}
                      >
                        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
                        <p className="mt-1 font-medium text-foreground">{item.title}</p>
                        <p className="mt-1 text-muted-foreground">{item.meta}</p>
                      </Link>
                    ))}
                    {isRecurringJob && !job.next_occurrence_info && (
                      <button
                        onClick={() => generateNextOccurrenceMutation.mutate()}
                        disabled={generateNextOccurrenceMutation.isPending}
                        className="flex items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/20 p-3 text-sm transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:opacity-50"
                        title="Generate next schedule in this recurring series"
                      >
                        {generateNextOccurrenceMutation.isPending ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : (
                          <Plus className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>
                    )}
                  </div>
                )}

                {job.recurrence_end && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Repeat until:</span>
                    <span>{formatDate(job.recurrence_end)}</span>
                  </div>
                )}

                {job.recurrence_parent_info ? (
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3 text-sm">
                    <p className="font-medium">Generated from</p>
                    <Link
                      to={entityDetailPath(job.recurrence_parent_info.id ?? job.id)}
                      className="mt-1 inline-flex text-primary hover:underline"
                    >
                      {formatJobTitleWithId({
                        jobId: job.recurrence_parent_info.id,
                        title: job.recurrence_parent_info.title,
                      })}
                    </Link>
                    <p className="mt-1 text-muted-foreground">
                      Status: {job.recurrence_parent_info.status?.replace('_', ' ') || 'Unknown'}
                    </p>
                  </div>
                ) : isRecurringJob ? (
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3 text-sm">
                    <p className="font-medium">Series source</p>
                    <p className="mt-1 text-muted-foreground">This job is the template/source for future occurrences.</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">This job is not configured to repeat.</p>
                )}

                {job.next_occurrence_info && (
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-3 text-sm">
                    <p className="font-medium">Next occurrence</p>
                    <Link
                      to={entityDetailPath(job.next_occurrence_info.id ?? job.id)}
                      className="mt-1 inline-flex text-primary hover:underline"
                    >
                      {formatJobTitleWithId({
                        jobId: job.next_occurrence_info.id,
                        title: job.next_occurrence_info.title,
                      })}
                    </Link>
                    <p className="mt-1 text-muted-foreground">
                      {job.next_occurrence_info.scheduled_start
                        ? `Scheduled for ${formatDate(job.next_occurrence_info.scheduled_start)}`
                        : 'Schedule not set'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
              <CardDescription>Jump to related sections</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" asChild>
                  <Link to={withReturnTo(withServiceInstanceId(`${isProjectPortal ? '/projects/assignments' : isEventPortal ? '/events/assignments' : '/assignments'}?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), currentReturnPath)}>
                    <Users className="mr-2 h-4 w-4" />
                    View Assignments
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={expensesPath}>
                    <Receipt className="mr-2 h-4 w-4" />
                    View Expenses
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={particularsPath}>
                    <FileText className="mr-2 h-4 w-4" />
                    View Inventory
                  </Link>
                </Button>
                <Button variant="outline" onClick={() => updateSearchParams({ tab: 'invoices' })}>
                  <Receipt className="mr-2 h-4 w-4" />
                  View Invoices
                </Button>
                <Button variant="outline" onClick={() => updateSearchParams({ tab: 'payments' })}>
                  <WalletCards className="mr-2 h-4 w-4" />
                  View Payments
                </Button>
                <Button variant="outline" asChild>
                  <Link to={withReturnTo(withServiceInstanceId(`/finance/worker-payments?job_id=${job.id}`, serviceInstanceId), currentReturnPath)}>
                    <WalletCards className="mr-2 h-4 w-4" />
                    Worker Payouts
                  </Link>
                </Button>
                <Button variant="outline" onClick={() => updateSearchParams({ tab: 'media' })}>
                  <ImageIcon className="mr-2 h-4 w-4" />
                  View Media
                </Button>
                <Button variant="outline" onClick={() => updateSearchParams({ tab: 'complaints' })}>
                  <MessageSquareWarning className="mr-2 h-4 w-4" />
                  View Complaints
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">Audit History</CardTitle>
                  <CardDescription>Recent activity for this job with optional action filtering.</CardDescription>
                </div>
                <div className="w-full sm:w-72">
                  <Label htmlFor="job-audit-action-filter" className="text-xs text-muted-foreground">Filter by action</Label>
                  <select
                    id="job-audit-action-filter"
                    className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={selectedAuditAction}
                    onChange={(event) => {
                      updateSearchParams({ audit_action: event.target.value || null })
                    }}
                  >
                    <option value="">All actions</option>
                    {(auditActionsQuery.data ?? []).map((action) => (
                      <option key={action} value={action}>{toAuditActionLabel(action)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {jobAuditsQuery.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton key={`audit-skeleton-${index}`} className="h-14 w-full rounded-lg" />
                  ))}
                </div>
              ) : jobAuditsQuery.data?.results?.length ? (
                <div className="overflow-hidden rounded-xl border border-border/70">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3">Action</th>
                        <th className="px-4 py-3">User</th>
                        <th className="px-4 py-3">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobAuditsQuery.data.results.map((audit) => (
                        <tr key={String(audit.id)} className="border-t border-border/60">
                          <td className="px-4 py-3 text-muted-foreground">
                            {audit.timestamp ? formatDate(audit.timestamp, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            }) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="font-normal">
                              {toAuditActionLabel(audit.action)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{audit.user_name || '-'}</td>
                          <td className="px-4 py-3 text-muted-foreground">{audit.value || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No audit entries found for the selected filter.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="media">
          <JobMediaPanel job={job} />
        </TabsContent>

        <TabsContent value="complaints">
          <ComplaintsPanel job={job} />
        </TabsContent>

        {isEventPortal ? (
          <TabsContent value="sub-events">
            <JobSubEventsPanel
              parentEventId={String(job.id)}
              serviceInstanceId={serviceInstanceId}
              returnPath={currentReturnPath}
            />
          </TabsContent>
        ) : null}

        <TabsContent value="assignments">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">Worker Assignments</CardTitle>
                  <CardDescription>
                    Workers currently assigned to this job.
                    {!assignmentsQuery.isLoading ? (
                      <>
                        {' '}Estimated payout: <span className="font-medium text-foreground">{formatCurrency(estimatedWorkerPayoutTotal)}</span>.
                        {' '}Actual payout: <span className="font-medium text-foreground">{formatCurrency(actualPayoutTotal)}</span>.
                      </>
                    ) : null}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" asChild>
                    <Link to={withReturnTo(withServiceInstanceId(`${isProjectPortal ? '/projects/assignments' : isEventPortal ? '/events/assignments' : '/assignments'}?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), currentReturnPath)}>
                      View All
                    </Link>
                  </Button>
                  <Button variant="outline" asChild disabled={jobModificationBlocked}>
                    <Link to={withReturnTo(withServiceInstanceId(`/assignments/create?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), currentReturnPath)}>
                      Manual form
                    </Link>
                  </Button>
                  <Button asChild disabled={jobModificationBlocked}>
                    <Link to={withReturnTo(withServiceInstanceId(`${isProjectPortal ? '/projects/assignments' : isEventPortal ? '/events/assignments' : '/assignments'}?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId), currentReturnPath)}>
                      <Users className="mr-2 h-4 w-4" />
                      Add from map
                    </Link>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <JobAssignCrewPanel
                jobId={job.id}
                serviceInstanceId={serviceInstanceId}
                assignments={assignmentsQuery.data?.results ?? []}
                disabled={jobModificationBlocked}
              />
              <JobAssignmentsTable
                jobId={job.id}
                serviceInstanceId={serviceInstanceId}
                currentReturnPath={currentReturnPath}
                assignments={assignmentsQuery.data?.results ?? []}
                isLoading={assignmentsQuery.isLoading}
                jobModificationBlocked={jobModificationBlocked}
                jobStatusChangeDisabled={jobStatusChangeDisabled}
                estimatedWorkerPayoutTotal={estimatedWorkerPayoutTotal}
                actualPayoutTotal={actualPayoutTotal}
                assignmentTableDate={assignmentTableDate}
                workDayEntries={jobWorkDayEntriesQuery.data?.results ?? []}
                formatAssignmentPayscale={formatAssignmentPayscale}
                onNavigate={navigate}
                onOpenWorkdayEntries={setAssignmentWorkdayEntries}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <JobInvoicesPanel
            jobId={String(job.id)}
            job={job}
            serviceInstanceId={serviceInstanceId}
            variant="embedded"
          />
        </TabsContent>

        <TabsContent value="payments">
          <JobPaymentsPanel
            jobId={String(job.id)}
            serviceInstanceId={serviceInstanceId}
          />
        </TabsContent>

        <TabsContent value="expenses">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">Expenses</CardTitle>
                  <CardDescription>
                    Costs, reimbursements, and supplier payments for this job.
                  </CardDescription>
                </div>
                <Button variant="outline" asChild>
                  <Link to={expensesPath}>
                    <Receipt className="mr-2 h-4 w-4" />
                    Manage Expenses
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <JobExpensesPanel jobId={String(job.id)} serviceInstanceId={serviceInstanceId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="particulars">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">Inventory</CardTitle>
                  <CardDescription>
                    Materials, supplies, and billable line items for this {entityLabel.toLowerCase()}.
                  </CardDescription>
                </div>
                <Button variant="outline" asChild>
                  <Link to={particularsPath}>
                    <FileText className="mr-2 h-4 w-4" />
                    Manage Inventory
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <JobInventoryPanel jobId={String(job.id)} serviceInstanceId={serviceInstanceId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workday-entries" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">{labels.workDayEntryPlural}</CardTitle>
                  <CardDescription>
                    Worker schedules and logged hours for this {entityLabel.toLowerCase()}.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setCreateWorkdayOpen(true)}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    Create {labels.workDayLower}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to={withReturnTo(withServiceInstanceId(`${labels.basePath}/workdays?job=${encodeURIComponent(String(job.id))}`, serviceInstanceId), currentReturnPath)}>
                      Open Calendar
                    </Link>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <JobWorkDayEntriesPanel
                jobId={String(job.id)}
                serviceInstanceId={serviceInstanceId}
                job={job}
                syncSelectedDateInUrl={activeTab === 'workday-entries'}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">This {entityLabel.toLowerCase()}</CardTitle>
              <CardDescription>
                Track required steps for this {entityLabel.toLowerCase()}. {entityLabel}-level items must be complete before marking the {entityLabel.toLowerCase()} completed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <JobChecklistPanel jobId={String(job.id)} kind={isProjectPortal ? 'project' : isEventPortal ? 'event' : 'job'} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Templates</CardTitle>
              <CardDescription>
                Reusable checklists applied automatically when new {labels.entityPluralLower} and {labels.workDayPluralLower} are created.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <JobChecklistTemplatePanel kind={isProjectPortal ? 'project' : 'job'} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={createWorkdayOpen} onOpenChange={setCreateWorkdayOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create {labels.workDayLower}</DialogTitle>
            <DialogDescription>
              Select the work date. Worker entries will use the planned start and end from the assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="create-workday-date">Work date</Label>
            <Input
              id="create-workday-date"
              type="date"
              value={createWorkdayDate}
              onChange={(event) => setCreateWorkdayDate(event.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateWorkdayOpen(false)}
              disabled={createWorkdayMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!createWorkdayDate) {
                  toast.error('Select a work date')
                  return
                }
                createWorkdayMutation.mutate()
              }}
              disabled={createWorkdayMutation.isPending}
            >
              {createWorkdayMutation.isPending ? 'Creating...' : `Create ${labels.workDayLower}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showJobCompletionBlockers} onOpenChange={setShowJobCompletionBlockers}>
        <DialogContent className="flex h-[min(94vh,calc(100vh-2rem))] w-[calc(100vw-2rem)] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl">
          <DialogHeader className="shrink-0 border-b border-border/60 px-6 pb-3 pt-6">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
              {entityLabel} cannot be completed yet
            </DialogTitle>
            <DialogDescription>
              Review or close these {labels.workDayEntryPluralLower} before marking the {entityLabel.toLowerCase()} complete.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              <p className="font-medium">
                {jobCompletionBlockingEntries.length} {labels.workDayEntryPluralLower} still pending.
              </p>
              {jobCompletionBlockingSummary ? (
                <p className="mt-1 text-xs text-amber-800">{jobCompletionBlockingSummary}</p>
              ) : null}
            </div>

            <div className="min-w-0 overflow-visible rounded-xl border border-border/70">
              <div className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
                Update pending entries directly from this list.
              </div>
              <table className="w-full table-fixed text-sm">
                <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="w-[8.5rem] px-4 py-3">Entry Ref</th>
                    <th className="px-4 py-3">Worker</th>
                    <th className="w-[7.5rem] px-4 py-3">Date</th>
                    <th className="w-[9.5rem] px-4 py-3">Status</th>
                    <th className="w-[18rem] px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {jobCompletionBlockingEntries.map((entry) => {
                    const status = resolveWorkDayEntryStatusFromEntry(entry) ?? entry.status
                    return (
                      <tr key={String(entry.id)} className="border-t border-border/60">
                        <td className="max-w-0 truncate px-4 py-3 font-medium text-foreground" title={entry.workday_entry_ref || `#${entry.id}`}>
                          {entry.workday_entry_ref || `#${entry.id}`}
                        </td>
                        <td className="max-w-0 truncate px-4 py-3 text-muted-foreground" title={entry.worker_name || `Worker #${entry.worker}`}>
                          {entry.worker_name || `Worker #${entry.worker}`}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{entry.work_date ? formatDate(entry.work_date) : '-'}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="max-w-full truncate border-amber-200 bg-amber-50 text-amber-700">
                            {entry.status_label || getWorkDayEntryStatusLabel(status)}
                          </Badge>
                        </td>
                        <td className="relative z-10 overflow-visible px-4 py-3 text-right">
                          <div className="flex flex-nowrap items-center justify-end gap-2">
                            {canEditWorkDayEntrySchedule(status) ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setScheduleEditEntry({
                                    entryId: String(entry.id),
                                    jobId: String(entry.job),
                                  })
                                }}
                              >
                                Edit schedule
                              </Button>
                            ) : null}
                            <Button variant="outline" size="sm" asChild>
                              <Link to={withReturnTo(withServiceInstanceId(`/workdays/${entry.id}/edit?focus=review&job=${job.id}`, serviceInstanceId), currentReturnPath)}>
                                Review
                              </Link>
                            </Button>
                            <WorkDayEntryActionsMenu
                              entry={{ ...entry, status }}
                              serviceInstanceId={serviceInstanceId}
                              alwaysShowTrigger
                              manageScheduleDialog={false}
                              onEditSchedule={() => {
                                setScheduleEditEntry({
                                  entryId: String(entry.id),
                                  jobId: String(entry.job),
                                })
                              }}
                              onStatusUpdated={() => {
                                queryClient.invalidateQueries({ queryKey: ['job-work-day-entries', String(id)] })
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                </table>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-border/60 px-6 py-4">
            <Button variant="outline" onClick={() => setShowJobCompletionBlockers(false)}>
              Close
            </Button>
            <Button asChild>
              <Link to={withServiceInstanceId(`${entityListPath}/${job.id}?tab=workday-entries`, serviceInstanceId)}>
                Open Workday Entries
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {scheduleEditEntry ? (
        <WorkDayEntryScheduleDialog
          entryId={scheduleEditEntry.entryId}
          jobId={scheduleEditEntry.jobId}
          serviceInstanceId={serviceInstanceId}
          open={Boolean(scheduleEditEntry)}
          onOpenChange={(open) => {
            if (!open) {
              setScheduleEditEntry(null)
            }
          }}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ['job-work-day-entries', String(id)] })
            await queryClient.invalidateQueries({ queryKey: ['workday-entry', scheduleEditEntry.entryId] })
          }}
        />
      ) : null}

      <ChecklistCompletionDialog
        open={showJobCompletionChecklist}
        onOpenChange={setShowJobCompletionChecklist}
        jobId={String(job.id)}
        kind={isProjectPortal ? 'project' : isEventPortal ? 'event' : 'job'}
        title="Complete job checklist"
        description="Confirm all required checklist steps are done before marking this job as completed."
        confirming={workflowStatusMutation.isPending}
        onConfirmed={() => {
          if (!pendingWorkflowCompletionTransition) {
            toast.error('No workflow completion transition is available for this job.')
            setShowJobCompletionChecklist(false)
            return
          }
          workflowStatusMutation.mutate(pendingWorkflowCompletionTransition, {
            onSuccess: () => {
              setPendingWorkflowCompletionTransition(null)
              setShowJobCompletionChecklist(false)
            },
          })
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open)
          if (!open && !deleteMutation.isPending) {
            setDeleteConfirmInput('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete ${entityLabel}`}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref, entityLabel: labels.entity })}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Type{' '}
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold text-foreground">
                {deleteConfirmationPhrase}
              </span>{' '}
              to confirm.
            </p>
            <Input
              value={deleteConfirmInput}
              onChange={(event) => setDeleteConfirmInput(event.target.value)}
              placeholder={deleteConfirmationPhrase}
              autoFocus
              disabled={deleteMutation.isPending}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter'
                  && deleteConfirmInput === deleteConfirmationPhrase
                  && !deleteMutation.isPending
                ) {
                  event.preventDefault()
                  const reasons = cancellationReasonsQuery.data ?? []
                  const reasonHint = reasons.length > 0
                    ? `Allowed reasons:\n${reasons.map((reason) => `- ${reason.label}`).join('\n')}`
                    : 'Examples: Booking cancelled, Resources unavailable'
                  const reason = window.prompt(`Enter reason.\n${reasonHint}`)
                  if (!reason?.trim()) {
                    toast.error(`Reason is required to delete this ${entityLabel.toLowerCase()}`)
                    return
                  }
                  deleteMutation.mutate({ id: job.id, reason: reason.trim() })
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                const reasons = cancellationReasonsQuery.data ?? []
                const reasonHint = reasons.length > 0
                  ? `Allowed reasons:\n${reasons.map((reason) => `- ${reason.label}`).join('\n')}`
                  : 'Examples: Booking cancelled, Resources unavailable'
                const reason = window.prompt(`Enter reason.\n${reasonHint}`)
                if (!reason?.trim()) {
                  toast.error('Reason is required to delete this job')
                  return
                }
                deleteMutation.mutate({ id: job.id, reason: reason.trim() })
              }}
              disabled={deleteConfirmInput !== deleteConfirmationPhrase || deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(assignmentWorkdayEntries)} onOpenChange={(open) => !open && setAssignmentWorkdayEntries(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Worker {labels.workDayEntryPluralLower}</DialogTitle>
            <DialogDescription>
              Entries for {assignmentWorkdayEntries?.worker_name || `worker #${assignmentWorkdayEntries?.worker_id ?? '-'}`} on this assignment.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const assignment = assignmentWorkdayEntries
            const rows = (jobWorkDayEntriesQuery.data?.results ?? []).filter((entry) => {
              if (!assignment?.worker_id || String(entry.worker ?? '') !== String(assignment.worker_id)) {
                return false
              }
              const entryAssignmentId = String(
                (entry as { assignment?: string | number | null; assignment_id?: string | number | null }).assignment
                  ?? (entry as { assignment_id?: string | number | null }).assignment_id
                  ?? '',
              )
              return !entryAssignmentId || entryAssignmentId === String(assignment.id)
            })

            return (
              <div className="space-y-3">
                <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3 text-sm">
                  <span className="font-medium">{rows.length}</span> {labels.workDayEntryPluralLower} found.
                </div>
                <div className="max-h-[60vh] overflow-auto rounded-xl border border-border/70">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Assignment Ref</th>
                        <th className="px-4 py-3">{labels.workDayEntry} Ref</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Logged Hours</th>
                        <th className="px-4 py-3">Actual Payout</th>
                        <th className="px-4 py-3">Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? rows.map((entry) => {
                        const status = resolveWorkDayEntryStatusFromEntry(entry) ?? entry.status
                        const showEntryPayout = isWorkerInvoicePayTypeForEntry(entry.pay_type || assignment?.pay_type || assignment?.pay_type_snapshot)
                        return (
                          <tr key={String(entry.id)} className="border-t border-border/60">
                            <td className="px-4 py-3 text-muted-foreground">{assignment?.assignment_ref || '-'}</td>
                            <td className="px-4 py-3 text-muted-foreground">{entry.workday_entry_ref || '-'}</td>
                            <td className="px-4 py-3">{entry.work_date ? formatDate(entry.work_date) : '-'}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline">
                                {entry.status_label || getWorkDayEntryStatusLabel(status)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {formatHoursTotal(Number(entry.hours ?? 0))}
                            </td>
                            <td className="px-4 py-3">
                              {showEntryPayout ? formatCurrency(Number(entry.total_pay ?? 0) || 0) : '-'}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {entry.reviewed_at ? `Reviewed ${formatDate(entry.reviewed_at)}` : 'Not reviewed'}
                            </td>
                          </tr>
                        )
                      }) : (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                            No workday entries found for this assignment worker.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignmentWorkdayEntries(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
